import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, List, ListItem, Avatar, IconButton, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MailIcon from '@mui/icons-material/Mail';
import { collection, query, where, onSnapshot, orderBy, limit, getDoc, doc, getDocs } from 'firebase/firestore';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';

import SendMessage from './SendMessage';
import MessagesList from './MessagesList';
import UserSearch from './UserSearch';
import { useUser } from '../UserContext';
import { db } from '../firebase';

const createConversationId = (userId1, userId2) => [userId1, userId2].sort().join('_');

export default function Conversation() {
  const { user } = useUser();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [recipientId, setRecipientId] = useState(location.state?.recipientId || null);
  const [recipientName, setRecipientName] = useState(location.state?.recipientName || '');
  const [recipientAvatar, setRecipientAvatar] = useState('');
  const [conversations, setConversations] = useState([]);
  const [newMessage, setNewMessage] = useState(false);

  // ------------------------------
  // 1) Load conversations
  // ------------------------------
  useEffect(() => {
    if (!user?.uid) return;

    const qConversations = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid),
      orderBy('lastUpdated', 'desc')
    );

    const unsubscribe = onSnapshot(qConversations, async (snapshot) => {
      const conversationDocs = snapshot.docs;
      const newConvos = await Promise.all(
        conversationDocs.map(async (cDoc) => {
          const cData = cDoc.data();
          if (!cData?.participants) return null;

          const otherParticipantId = cData.participants.find((id) => id !== user.uid);
          const otherUserSnap = await getDoc(doc(db, 'users', otherParticipantId));
          const otherUser = otherUserSnap.data() || {};

          const recipientName = `${otherUser.firstName || 'Unknown'} ${otherUser.lastName || ''}`.trim();

          const msgQuery = query(
            collection(db, `conversations/${cDoc.id}/messages`),
            orderBy('timestamp', 'desc'),
            limit(1)
          );
          const msgSnap = await getDocs(msgQuery);
          const lastMsg = msgSnap.docs.length ? msgSnap.docs[0].data().message : '';

          const unreadQuery = query(
            collection(db, `conversations/${cDoc.id}/messages`),
            where('recipientId', '==', user.uid),
            where('read', '==', false)
          );
          const unreadSnap = await getDocs(unreadQuery);
          const hasUnreadMessages = !unreadSnap.empty;

          return {
            id: cDoc.id,
            participants: cData.participants,
            recipientName,
            recipientAvatar: otherUser.photoURL || '/default-avatar.png',
            lastMessage: lastMsg,
            hasUnreadMessages,
            lastUpdated: cData.lastUpdated,
          };
        })
      );

      const filtered = newConvos.filter(Boolean);
      const sorted = filtered.sort((a, b) => {
        if (!a.lastUpdated || !b.lastUpdated) return 0;
        return b.lastUpdated.seconds - a.lastUpdated.seconds;
      });

      setConversations(sorted);
    });

    return () => unsubscribe();
  }, [user]);

  // ------------------------------
  // 2) Start new message
  // ------------------------------
  const handleNewMessage = () => {
    setNewMessage(true);
    setRecipientId(null);
  };
  const handleCancelNewMessage = () => {
    setNewMessage(false);
  };
  const handleUserSelect = (id, username) => {
    setRecipientId(id);
    setRecipientName(username);
    setNewMessage(false);
  };

  // ------------------------------
  // 3) Select conversation
  // ------------------------------
  const handleConversationSelect = (convo) => {
    if (!convo?.participants) return;

    const otherId = convo.participants.find((id) => id !== user.uid);
    setRecipientId(otherId);
    setRecipientName(convo.recipientName);
    setRecipientAvatar(convo.recipientAvatar);
    setNewMessage(false);

    setConversations((prev) =>
      prev.map((c) => (c.id === convo.id ? { ...c, hasUnreadMessages: false } : c))
    );
  };

  // mobile "back to list"
  const handleBackToList = () => {
    setRecipientId(null);
    setNewMessage(false);
  };

  const renderMainContent = () => {
    if (newMessage) {
      return (
        <Box bgcolor="#eceff1" p={2} display="flex" alignItems="center">
          {isMobile && (
            <IconButton onClick={handleCancelNewMessage} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <UserSearch onSelectUser={handleUserSelect} />
        </Box>
      );
    }

    if (recipientId) {
      return (
        <>
          <Box
            bgcolor="#eceff1"
            p={2}
            boxShadow={1}
            display="flex"
            alignItems="center"
          >
            {isMobile && (
              <IconButton onClick={handleBackToList} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Avatar
              alt={recipientName}
              src={recipientAvatar || '/default-avatar.png'}
              sx={{ mr: 2 }}
            />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {recipientName}
            </Typography>
          </Box>
          <Box sx={{ p: 1, borderTop: '1px solid #ddd' }}>
            <SendMessage
              conversationId={createConversationId(user.uid, recipientId)}
              recipientName={recipientName}
              recipientId={recipientId}
            />
          </Box>
          <Box display="flex" flexDirection="column" flexGrow={1} overflow="auto">
            <MessagesList
              conversationId={createConversationId(user.uid, recipientId)}
              recipientName={recipientName}
              recipientId={recipientId}
            />
          </Box>

          
        </>
      );
    }

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexGrow={1}
      >
        <Typography variant="h6" color="text.secondary">
          Select a conversation or start a new message
        </Typography>
      </Box>
    );
  };

  return (
    <Box display="flex" height="100vh" flexDirection={{ xs: 'column', sm: 'row' }}>
      {!isMobile || (!recipientId && !newMessage) ? (
        <Box
          width={{ xs: '100%', sm: '320px' }}
          bgcolor="#f5f5f5"
          p={2}
          display="flex"
          flexDirection="column"
          sx={{ height: '98vh' }}
        >
          <Typography variant="h6" gutterBottom>
            Messages
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewMessage}
            sx={{ mb: 2 }}
          >
            New Message
          </Button>

          <List sx={{ width: '100%', flexGrow: 1, overflowY: 'auto' }}>
            {conversations.map((convo) => (
              <ListItem
                button
                key={convo.id}
                onClick={() => handleConversationSelect(convo)}
                sx={{
                  backgroundColor: convo.hasUnreadMessages ? '#ffe0b2' : 'transparent',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  mb: 1,
                  '&:hover': {
                    backgroundColor: convo.hasUnreadMessages ? '#ffcc80' : '#eaeaea',
                  },
                }}
              >
                <Box display="flex" alignItems="center" flexGrow={1}>
                  <Avatar
                    alt={convo.recipientName}
                    src={convo.recipientAvatar}
                    sx={{ mr: 2 }}
                  />
                  <Box display="flex" flexDirection="column">
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }} noWrap>
                      {convo.recipientName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {convo.lastMessage || 'No messages yet'}
                    </Typography>
                  </Box>
                </Box>

                {convo.hasUnreadMessages && (
                  <MailIcon sx={{ color: 'darkorange', ml: 'auto' }} />
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}

      <Box display="flex" flexDirection="column" flexGrow={1}>
        {renderMainContent()}
      </Box>
    </Box>
  );
}
